import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuthCtx } from '../App';
import { toast } from 'react-toastify';
import PasswordInput from '../components/ui/PasswordInput';

export default function LoginPage() {
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');
  const { login, loading } = useAuthCtx();
  const navigate  = useNavigate();
  const location  = useLocation();

  const dashboardFor = (role) => (role === 'admin' ? '/admin' : '/dashboard');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const { user } = await login(email, password);
      toast.success('Welcome back!');
      const dest = location.state?.from || dashboardFor(user.role);
      navigate(dest, { replace: true });
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed');
    }
  };

  return (
    <div className="min-h-screen flex bg-tea-50">
      {/* Brand panel */}
      <div className="hidden lg:flex w-1/2 relative overflow-hidden text-white flex-col justify-between p-12"
           style={{ background: 'linear-gradient(135deg,#f472b6 0%,#db2777 50%,#7a1746 100%)' }}>
        <div className="absolute -right-16 -top-16 h-72 w-72 rounded-full bg-white/15 blur-2xl" />
        <div className="absolute -left-10 bottom-10 h-56 w-56 rounded-full border-[20px] border-white/10" />
        <Link to="/" className="relative inline-flex items-center gap-2 font-extrabold text-2xl">
          <span aria-hidden="true" className="grid place-items-center h-9 w-9 rounded-xl bg-white/20">
            <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor"><path d="M12 2c2 4 6 5 6 9a6 6 0 0 1-12 0c0-4 4-5 6-9z" /></svg>
          </span>
          AB8 Beauty
        </Link>
        <div className="relative">
          <h2 className="text-4xl font-extrabold leading-tight">Skincare,<br/>bottled beautifully.</h2>
          <p className="mt-4 text-white/85 max-w-sm">
            Sign in to track your orders, manage your glow rituals, and earn 3% / 2% / 1% referral cash.
          </p>
        </div>
        <p className="relative text-xs text-white/70">© {new Date().getFullYear()} AB8 Beauty &amp; Skin</p>
      </div>

      {/* Form panel */}
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <Link to="/" aria-label="AB8 Beauty home" className="lg:hidden inline-flex items-center justify-center mb-4">
              <img src="/logo-ab8.svg" alt="AB8 Beauty &amp; Skin" className="h-20 w-auto" />
            </Link>
            <h1 className="text-3xl font-extrabold text-gray-900">Welcome back</h1>
            <p className="text-gray-500 mt-1">Sign in to your AB8 Beauty account.</p>
          </div>

          <form onSubmit={handleSubmit} className="bg-white rounded-3xl p-7 shadow-[0_10px_30px_rgba(190,24,93,0.08)] space-y-4">
            {error && (
              <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm">{error}</div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                required className="input-field" placeholder="you@example.com" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <PasswordInput value={password} onChange={e => setPassword(e.target.value)}
                required placeholder="••••••••" />
            </div>
            <div className="flex items-center justify-end">
              <Link to="/forgot-password" className="text-sm text-tea-600 hover:underline">
                Forgot password?
              </Link>
            </div>
            <button type="submit" disabled={loading} className="btn-primary w-full py-3">
              {loading ? 'Signing in…' : 'Sign In'}
            </button>
          </form>

          <p className="text-center mt-6 text-gray-600 text-sm">
            New to AB8 Beauty?{' '}
            <Link to="/register" className="text-tea-600 hover:underline font-semibold">Create an account</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
